/**
 * AppSwitcher — the nine-dot app launcher in the top bar's right corner.
 *
 * Lists the apps the signed-in role can open, each as a row with a round, brand-
 * tinted icon, its name and a one-line description. It starts out thin (most roles
 * have one or two apps today) and grows as the apps split out. Gating
 * mirrors the sidebar (DrawerComponents.js), so an app shows here only if it shows there,
 * in the same order and with the same icon. The one exception is CareMap: it is its own
 * full-screen app outside the dashboard, so it lives here and not in the sidebar.
 */

import React, { useState } from "react";
import { masterChartPath } from "constants/masterChartRoutes";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Popover,
  SvgIcon,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import RecordVoiceOverRounded from "@mui/icons-material/RecordVoiceOverRounded";
import SchoolRounded from "@mui/icons-material/SchoolRounded";
import HealthAndSafetyRounded from "@mui/icons-material/HealthAndSafetyRounded";
import NoteAltRounded from "@mui/icons-material/NoteAltRounded";
import ForumRounded from "@mui/icons-material/ForumRounded";
import QuestionAnswerRounded from "@mui/icons-material/QuestionAnswerRounded";
import LocalHospitalRounded from "@mui/icons-material/LocalHospitalRounded";
import FactCheckRounded from "@mui/icons-material/FactCheckRounded";
import PsychologyRounded from "@mui/icons-material/PsychologyRounded";
import MonitorHeartRounded from "@mui/icons-material/MonitorHeartRounded";
import VerifiedRounded from "@mui/icons-material/VerifiedRounded";
import PeopleRounded from "@mui/icons-material/PeopleRounded";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import BusinessRounded from "@mui/icons-material/BusinessRounded";
import MapRounded from "@mui/icons-material/MapRounded";
import { useAuth } from "hooks/useAuth";
import { isDevEnvironment } from "utils/isDevEnvironment";

// Google's launcher dots: nine circles. @mui/icons-material only has square-dot grids
// (Apps, AppsRounded), so this one is drawn with SvgIcon.
const AppsDotsIcon = (props) => (
  <SvgIcon {...props}>
    {[5, 12, 19].flatMap((cy) =>
      [5, 12, 19].map((cx) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={2.2} />),
    )}
  </SvgIcon>
);

// Each app's description has a "home" namespace key (descriptionKey), translated at
// render, and so does a name that is not a product name (nameKey). The staff apps' names
// and section titles stay in English, as they are in the sidebar.
const APPS = {
  caremap: {
    // Its own section on the role Home pages: it is added after every role's apps,
    // and without a group it would trail the last section with no heading of its own.
    group: "Find care",
    name: "CareMap",
    description: "Find care near you",
    descriptionKey: "apps.caremap",
    icon: <MapRounded />,
    path: "/caremap",
  },
  carestart: {
    name: "CareStart",
    description: "Talk through your symptoms",
    descriptionKey: "apps.carestart",
    icon: <RecordVoiceOverRounded />,
    path: "/dashboard/carestart",
  },
  medicalLibrary: {
    name: "Medical Library",
    nameKey: "apps.medicalLibraryName",
    description: "Trusted health information",
    descriptionKey: "apps.medicalLibrary",
    icon: <SchoolRounded />,
    path: "/dashboard/medical-library",
  },
  medicalRecord: {
    name: "MasterChart",
    description: "Your health history in one place",
    descriptionKey: "apps.medicalRecord",
    icon: <HealthAndSafetyRounded />,
    path: masterChartPath(),
  },
  chartmind: {
    group: "Clinical",
    name: "ChartMind",
    description: "AI notes for every visit",
    descriptionKey: "apps.chartmind",
    icon: <NoteAltRounded />,
    path: "/dashboard/chartmind",
  },
  peerview: {
    group: "Clinical",
    name: "PeerView",
    description: "Discuss cases with colleagues",
    descriptionKey: "apps.peerview",
    icon: <ForumRounded />,
    path: "/dashboard/peerview",
  },
  askMeAnything: {
    group: "Education",
    name: "Ask Me Anything",
    description: "Clinical questions, answered",
    descriptionKey: "apps.askMeAnything",
    icon: <QuestionAnswerRounded />,
    path: "/dashboard/ask-me-anything",
  },
  morningReport: {
    group: "Education",
    name: "MorningReport",
    description: "Daily cases to learn from",
    descriptionKey: "apps.morningReport",
    icon: <SchoolRounded />,
    path: "/dashboard/morning-report",
  },
  visitstart: {
    name: "VisitStart",
    description: "Patient check-in for your facility",
    descriptionKey: "apps.visitstart",
    icon: <FactCheckRounded />,
    path: "/dashboard/visitstart",
  },
  prompts: {
    group: "Content",
    name: "Prompts",
    description: "Manage the AI prompts",
    descriptionKey: "apps.prompts",
    icon: <PsychologyRounded />,
    path: "/dashboard/prompts",
  },
  morningReportCases: {
    group: "Content",
    name: "MorningReport Cases",
    description: "Write and publish cases",
    descriptionKey: "apps.morningReportCases",
    icon: <LocalHospitalRounded />,
    path: "/dashboard/admin/morning-report-cases",
  },
  systemHealth: {
    group: "Monitoring",
    name: "System Health",
    description: "Uptime and health checks",
    descriptionKey: "apps.systemHealth",
    icon: <MonitorHeartRounded />,
    path: "/dashboard/admin/health",
  },
  locationApproval: {
    group: "People",
    name: "Location Approval",
    description: "Approve new facility locations",
    descriptionKey: "apps.locationApproval",
    icon: <VerifiedRounded />,
    path: "/dashboard/admin/approval",
  },
  regionalAdmins: {
    group: "People",
    name: "Regional admins",
    description: "Manage regional prompt editors",
    descriptionKey: "apps.regionalAdmins",
    icon: <PeopleRounded />,
    path: "/dashboard/admin/regional-admins",
  },
  usageMonitor: {
    group: "Monitoring",
    name: "Usage Monitor",
    description: "AI usage and costs",
    descriptionKey: "apps.usageMonitor",
    icon: <BarChartRounded />,
    path: "/dashboard/admin/usage",
  },
  hipaaCompliance: {
    group: "Monitoring",
    name: "HIPAA Compliance",
    description: "Compliance tasks and evidence",
    descriptionKey: "apps.hipaaCompliance",
    icon: <FactCheckRounded />,
    path: "/dashboard/admin/hipaa-compliance",
  },
  // A ChartMind manager's org tools.
  team: {
    name: "Team",
    description: "Manage your team",
    descriptionKey: "apps.team",
    icon: <BusinessRounded />,
    path: "/dashboard/team",
  },
  orgUsage: {
    name: "Usage",
    description: "Encounters and team activity",
    descriptionKey: "apps.orgUsage",
    icon: <BarChartRounded />,
    path: "/dashboard/usage",
  },
};

/**
 * Apps split into the sidebar's sections (an app's `group`), in first-seen order.
 * Roles whose apps have no group come back as one untitled section.
 */
export const groupApps = (apps) =>
  apps.reduce((sections, app) => {
    const title = app.group || null;
    const section = sections.find((s) => s.title === title);
    if (section) section.apps.push(app);
    else sections.push({ title, apps: [app] });
    return sections;
  }, []);

/** The apps the signed-in role can open. Shared with the role Home page (HomePage.jsx). */
export const useRoleApps = () => {
  const apps = useSidebarApps();
  const { userData, isGlobalAdmin, isRegionalAdmin } = useAuth();
  // CareMap is for every role but MorningReport learners, and only on dev servers until
  // it is ready for production (its route is gated the same way in AppRoutes.jsx).
  const isLearner = !isGlobalAdmin && !isRegionalAdmin && userData?.role === "learner";
  if (apps.length === 0 || isLearner || !isDevEnvironment()) return apps;
  return [...apps, APPS.caremap];
};

/** The role's apps that also have a sidebar entry. */
const useSidebarApps = () => {
  const { userData, isGlobalAdmin, isRegionalAdmin } = useAuth();

  if (isGlobalAdmin) {
    return [
      APPS.prompts,
      APPS.morningReportCases,
      APPS.locationApproval,
      APPS.regionalAdmins,
      APPS.usageMonitor,
      APPS.systemHealth,
      APPS.hipaaCompliance,
    ];
  }
  if (isRegionalAdmin) return [APPS.prompts];

  switch (userData?.role) {
    case "patient":
      return [APPS.carestart, APPS.medicalLibrary, APPS.medicalRecord];
    case "professional":
      return [
        APPS.chartmind,
        APPS.peerview,
        // MorningReport is off for professionals for now; learners have it.
        // [BETA-KIJABE] Kijabe beta testers only, as in the sidebar.
        userData?.betaAccess && APPS.askMeAnything,
      ].filter(Boolean);
    case "learner":
      return [APPS.morningReport];
    case "facility":
      return [APPS.visitstart];
    case "admin":
      return [APPS.prompts, APPS.team, APPS.orgUsage];
    default:
      return [];
  }
};

/**
 * @param {Function} [onNavigate] called before navigating, e.g. to close a mobile drawer.
 * @param {boolean} [opensDown] open the list below the button, right-aligned, for a launcher
 *   in a top-right corner as Google has it (CareMap) rather than at the foot of the sidebar.
 */
const AppSwitcher = ({ onNavigate, opensDown = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation("home");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const apps = useRoleApps();

  if (apps.length === 0) return null;

  const open = (path) => {
    setAnchorEl(null);
    onNavigate?.();
    navigate(path);
  };

  return (
    <>
      <Tooltip title={t("apps.label")}>
        <IconButton
          aria-label={t("apps.label")}
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl)}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          <AppsDotsIcon />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        // Opens downward from a top corner; otherwise (CareMap's sidebar foot) upward.
        anchorOrigin={
          opensDown
            ? { vertical: "bottom", horizontal: "right" }
            : { vertical: "top", horizontal: "left" }
        }
        transformOrigin={
          opensDown
            ? { vertical: "top", horizontal: "right" }
            : { vertical: "bottom", horizontal: "left" }
        }
        slotProps={{
          paper: {
            sx: {
              width: 320,
              maxWidth: "calc(100vw - 16px)",
              maxHeight: "min(600px, calc(100dvh - 96px))",
              borderRadius: 3,
              ...(opensDown ? { mt: 1 } : { mb: 1 }),
            },
          },
        }}
      >
        {/* A plain list in the sidebar's order, each app with its one-line
            description. No section headings: with a handful of apps they cost more
            than they explain. The brand shows in the icons: each sits in a soft
            primary-tinted circle, and the app you are in gets a solid one. */}
        <List component="nav" aria-label={t("apps.label")} sx={{ p: 1 }}>
          {apps.map((app) => {
            const current = pathname.startsWith(app.path);
            return (
              <ListItemButton
                key={app.path}
                selected={current}
                onClick={() => open(app.path)}
                aria-current={current ? "page" : undefined}
                sx={{ borderRadius: 2, px: 1.5, my: "2px" }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={({ palette }) => ({
                      width: 40,
                      height: 40,
                      bgcolor: current
                        ? palette.primary.main
                        : alpha(palette.primary.main, 0.12),
                      color: current ? palette.primary.contrastText : palette.primary.main,
                    })}
                  >
                    {app.icon}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={app.nameKey ? t(app.nameKey) : app.name}
                  secondary={app.descriptionKey ? t(app.descriptionKey) : app.description}
                  primaryTypographyProps={{ variant: "subtitle2" }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Popover>
    </>
  );
};

export default AppSwitcher;
